import { toBlob } from 'html-to-image';
import React, { useEffect, useState } from 'react';
import { getCompactor, Responsive, useContainerWidth } from 'react-grid-layout';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/arrow-left.svg';
import { CustomDashboardWidget } from '../../../../interface/dashboard.interface';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import dashboard from '../../../../utils/api/custom-dashboard';
import sidebarData from '../data/dashboard-sidebar.json';

import DraggableChartWidget from './DraggableChartWidget';
import SaveDashboardModal from './SaveDashboardModal';
import SidebarModule from './SidebarModule';

/**
 * Handles keyboard events for interactive elements.
 * @param {any} event - The keyboard event.
 * @param {any} action - The action to perform.
 */
const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

// interface ModalInterface {
//   title: string;
//   header: boolean;
//   content: JSX.Element;
//   version: string;
//   rightCorner: string;
// }
/**
 * Generates a unique widget ID using the crypto API.
 * @returns {string} A unique widget ID.
 */
const generateWidgetId = () => crypto.randomUUID();

/**
 * Create New Dashboard view with dynamic JSON loading and interactive workspace.
 * @returns {any} The rendered component.
 */
function CreateNewDashboard() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { containerRef, width } = (useContainerWidth as any)();
  // const workspaceReference = useRef<HTMLDivElement>(null);
  const [activeWidgets, setActiveWidgets] = useState<CustomDashboardWidget[]>([]);
  const [selectedCharts, setSelectedCharts] = useState<string[]>([]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [initialFormData, setInitialFormData] = useState<any>(null);

  useEffect(() => {
    if (isEditMode && id) {
      dashboard
        .getCustomDashboardDetails(id)
        .then((response) => {
          const data = response?.data;
          setInitialFormData({
            name: data.name,
            showTo: data.showTo,
            showAsTab: data.showAsTab,
            isDefault: data.isDefault,
          });

          if (data.widgets) {
            const widgets = Array.isArray(data.widgets) ? data.widgets : JSON.parse(data.widgets);
            setActiveWidgets(widgets);
            setSelectedCharts(widgets.map((w: any) => w.sidebarId).filter(Boolean));
          }
        })
        .catch((error) => {
          console.error('Error fetching dashboard details:', error);
          dispatch(
            setNotificationMessage({
              status: true,
              message: 'Failed to fetch dashboard details',
              type: 'error',
              code: 0,
            }),
          );
        });
    }
  }, [id, isEditMode]);

  /**
   * Toggles a chart in the workspace.
   * @param {string} chartId - Unique ID for the chart.
   * @param {any} chartConfig - Configuration from JSON.
   */
  const toggleChart = (chartId: string, chartConfig: any) => {
    if (selectedCharts.includes(chartId)) {
      setSelectedCharts((previous) => previous.filter((idValue) => idValue !== chartId));
      setActiveWidgets((previous) => previous.filter((w) => w.sidebarId !== chartId));
    } else {
      const resolvedType = chartConfig.chart_type || chartConfig.type;
      const uniqueId = generateWidgetId();

      /**
       * Gets the initial size for a widget based on its type in grid cells.
       * Matches the logic in the user provided snippet.
       * @param {string} type - The widget type.
       * @param {string} subheading - The widget heading.
       * @returns {any} } The initial grid size.
       */
      const getInitialSize = (type: string, subheading: string) => {
        const typeKey = type.toLowerCase();
        console.log(typeKey, subheading);
        // Map logic
        if (typeKey === 'map') return { w: 6, h: 5 };
        if (typeKey === 'count_card') return { w: 3, h: 2 };

        // ✅ Case 4: Default (other charts)
        return { w: 6, h: 5 };
      };

      /**
       * Calculates the next position to avoid overlapping with existing widgets in grid cells.
       * @param {any} widgets - List of current widgets.
       * @returns {any} } The next available grid position.
       */
      const getNextPosition = (widgets: CustomDashboardWidget[]) => {
        const maxY = widgets.reduce((max, widget) => {
          const bottom = widget.y + widget.h;
          return bottom > max ? bottom : max;
        }, 0);
        return { x: 0, y: maxY };
      };

      const widgetSize = getInitialSize(resolvedType, chartConfig.heading);
      const nextPos = getNextPosition(activeWidgets);

      setSelectedCharts((previousSelected) => [...previousSelected, chartId]);
      setActiveWidgets((previousWidgets) => [
        ...previousWidgets,
        {
          id: uniqueId,
          sidebarId: chartId,
          type: resolvedType,
          data: chartConfig.chartdata || chartConfig.data,
          heading: chartConfig.heading,
          x: nextPos.x,
          y: nextPos.y,
          w: widgetSize.w,
          h: widgetSize.h,
        },
      ]);
    }
  };

  /**
   * Handles layout change from react-grid-layout.
   * @param {any[]} currentLayout - The updated layout from RGL.
   */
  const handleLayoutChange = (currentLayout: any[]) => {
    setActiveWidgets((previous) =>
      previous.map((widget) => {
        const layoutItem = currentLayout.find((item) => item.i === widget.id);
        if (layoutItem) {
          return {
            ...widget,
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          };
        }
        return widget;
      }),
    );
  };

  /**
   * Removes a widget from the workspace.
   * @param {string} idValue - Widget ID.
   */
  const handleRemoveWidget = (idValue: string) => {
    setActiveWidgets((previous) => {
      const widgetToRemove = previous.find((w) => w.id === idValue);
      if (widgetToRemove) {
        setSelectedCharts((previousSelected) =>
          previousSelected.filter((chartId) => chartId !== widgetToRemove.sidebarId),
        );
      }
      return previous.filter((widget) => widget.id !== idValue);
    });
  };
  /**
   * Handles the create dashboard action.
   * @param {any} data - Dashboard data to be created.
   */
  const handleCreateDashboard = async (data: any) => {
    setIsCreating(true);
    console.log('Creating Dashboard Data:', data);
    let file: Blob | null = null;
    if (containerRef.current) {
      // Give a small delay to ensure the grid and charts are fully rendered
      await new Promise((resolve) => {
        setTimeout(resolve, 800);
      });

      const node = containerRef.current;
      const actualWidth = node.offsetWidth || width || 1200;
      const actualHeight = node.offsetHeight || 570;

      try {
        file = await toBlob(node, {
          cacheBust: true,
          skipFonts: true,
          width: actualWidth,
          height: actualHeight,
          imagePlaceholder:
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==',
          /**
           * Filters out elements that should not be included in the screenshot.
           * @param {any} nodeElement - The DOM element to filter.
           * @returns {any} True if the element should be included, false otherwise.
           */
          filter: (nodeElement: HTMLElement) => {
            const tag = nodeElement.tagName?.toLowerCase();
            if (tag === 'script' || tag === 'iframe') return false;
            // Skip Google Maps elements which are the most common source of capture failures
            const className = nodeElement.className || '';
            if (
              typeof className === 'string' &&
              (className.includes('gm-') || className.includes('gmnoprint'))
            )
              return false;
            if (tag === 'map') return false;
            return true;
          },
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
            width: `${actualWidth}px`,
            height: `${actualHeight}px`,
          },
        });
      } catch (captureError: any) {
        console.error('Core capture failed, attempting without images:', captureError);
        try {
          // Fallback: try capture with even more aggressive filtering if it fails
          file = await toBlob(node, {
            width: actualWidth,
            height: actualHeight,
            skipFonts: true,
            /**
             * Filters out elements that should not be included in the screenshot.
             * @param {any} nodeElement - The DOM element to filter.
             * @returns {any} True if the element should be included, false otherwise.
             */
            filter: (nodeElement: HTMLElement) => {
              const tag = nodeElement.tagName?.toLowerCase();
              return tag !== 'img' && tag !== 'script' && tag !== 'iframe';
            },
          });
        } catch (fallbackError) {
          console.error(
            'Fallback capture also failed, proceeding without screenshot:',
            fallbackError,
          );
          file = null;
        }
      }
    }
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('showTo', data.showTo);
      formData.append('showAsTab', data.showAsTab);
      formData.append('isDefault', data.isDefault);
      formData.append('widgets', JSON.stringify(activeWidgets));
      if (file) {
        formData.append('customDashboardScreenshot', file, 'screenshot.png');
      }

      const apiCall =
        isEditMode && id
          ? dashboard.updateCustomDashboard(id, formData, true)
          : dashboard.createCustomDashboard(formData, true);

      apiCall
        .then((response) => {
          console.log(`Dashboard ${isEditMode ? 'updated' : 'created'} successfully:`, response);
          setIsSaveModalOpen(false);
          setIsCreating(false);
          navigate('/ev-admin/dashboard/customize-dashboard');
        })
        .catch((error) => {
          console.error(`Error ${isEditMode ? 'updating' : 'creating'} dashboard:`, error);
          setIsCreating(false);
          dispatch(
            setNotificationMessage({
              status: true,
              message:
                error?.response?.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} dashboard`,
              type: 'error',
              code: 0,
            }),
          );
        });
    } catch (error: any) {
      console.error('Final screenshot capture failure:', error);
      setIsCreating(false);
      dispatch(
        setNotificationMessage({
          status: true,
          message: 'Failed to capture dashboard screenshot',
          type: 'error',
          code: 0,
        }),
      );
    }
  };
  /**
   * Handles the save dashboard action by opening the modal.
   */
  const handleSaveDashboard = () => {
    setIsSaveModalOpen(true);
  };

  // Prepare layout for RGL matching the user provided pattern
  const layout = activeWidgets.map((w) => {
    const widgetType = w.type.toLowerCase();
    const isSpecial =
      w.heading === 'Request & Approval Overview' ||
      widgetType === 'calendar' ||
      widgetType === 'calender' ||
      widgetType === 'table' ||
      widgetType === 'alert_list';

    let minW = 6;
    if (widgetType === 'table' || widgetType === 'alert_list' || widgetType === 'map') {
      minW = 12;
    }

    return {
      i: w.id,
      x: w.x,
      y: w.y,
      w: w.w,
      h: w.h,
      minW,
      minH: isSpecial ? 6 : 4,
    };
  });

  return (
    <div className="create-new-dashboard-wrap">
      <ModalBox
        title={t('customizeDashboard')}
        open={isSaveModalOpen}
        closeModal={() => setIsSaveModalOpen(false)}
        content={
          <SaveDashboardModal
            onClose={() => setIsSaveModalOpen(false)}
            onSave={(saveData: any) => handleCreateDashboard(saveData)}
            loading={isCreating}
            setLoading={setIsCreating}
            initialData={initialFormData}
          />
        }
        header
        version="v2"
        rightCorner="rounded"
      />
      <div className="customize-header">
        <div className="header-left">
          <ArrowLeftIcon
            className="back-arrow"
            onClick={() => navigate('/ev-admin/dashboard/customize-dashboard')}
            onKeyDown={(event) =>
              handleKeyDown(event, () => navigate('/ev-admin/dashboard/customize-dashboard'))
            }
            role="button"
            tabIndex={0}
            aria-label={t('back')}
          />
          <div className="customize-title">
            <span>{isEditMode ? t('edit') : t('customize')}</span>
            <span>{t('dashboard')}</span>
          </div>
        </div>
        <div className="header-right">
          <MyButton
            label={t('save')}
            buttonType="submit"
            disableButton={activeWidgets.length === 0}
            onClickFunc={handleSaveDashboard}
          />
        </div>
      </div>

      <div className="create-new-main">
        <div className="create-new-sidebar">
          {sidebarData.map((module: any) => (
            <SidebarModule
              key={module.id}
              module={module}
              selectedCharts={selectedCharts}
              onToggleChart={toggleChart}
            />
          ))}
        </div>

        <div className="create-new-content">
          <div className="customize-description">
            <div className="description-content">
              <h2>{t('customizeYourDashboard')}</h2>
              <p>{t('customizeDashboardDescription')}</p>
            </div>
          </div>

          <div className="workspace-area" ref={containerRef}>
            <Responsive
              className="layout"
              layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
              rowHeight={80}
              width={width}
              margin={[12, 12]}
              containerPadding={[12, 12]}
              resizeConfig={{ enabled: false }}
              autoSize
              compactor={getCompactor(null, false, true)}
              dragConfig={{
                enabled: true,
                bounded: true,
                cancel: '.no-drag',
              }}
              onLayoutChange={(current: any) => handleLayoutChange(current)}>
              {activeWidgets.map((widget) => (
                <div key={widget.id}>
                  <DraggableChartWidget
                    id={widget.id}
                    type={widget.type}
                    data={widget.data}
                    heading={widget.heading}
                    onRemove={handleRemoveWidget}
                  />
                </div>
              ))}
            </Responsive>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateNewDashboard;
